import { useEffect, useState, type ReactNode } from 'react'
import { PageHead } from '@components/PageHead'
import { useEquivalentDomains } from '@features/equivalent-domains/hooks/useEquivalentDomains'
import { useFolders } from '@features/vault/hooks/useFolders'
import { VaultItemAvatar } from '@features/vault/components/VaultItemAvatar'
import { VaultStrengthBadge } from '@features/vault/components/VaultStrengthBadge'
import { groupIdForItem } from '@features/vault/services/vaultDomainGroups'
import { useTotp, type UseTotpState } from '@features/vault/hooks/useTotp'
import { useVaultDetail } from '@features/vault/hooks/useVaultDetail'
import { type VaultCustomField, type VaultItemPayload } from '@features/vault/types'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { formatDateTimeJst } from '@utils/datetime'
import { Notice } from '@shared/ui/Notice'
import { TotpRing } from '@shared/ui/TotpRing'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 個別 Vault アイテム詳細画面 (T-D-Vault-C、xlsx No.29、L-6 vault/:id route 解消)。
 *
 * design/js/screens-vault.jsx (VaultDetail) を移植。本 commit のスコープ:
 *   - 復号後の type-conditional kv 描画 (login / card / note / identity)
 *   - パスワード mask / show 切替 (eye/eye-off icon)
 *   - 各 field の copy ボタン (clipboard 90s 自動クリア)、TOTP は base32
 *     シークレットのみ copy (live OTP rotation は @noble/hashes commit で対応)
 *   - URL は external icon で新タブ open (rel="noopener noreferrer")
 *   - 戻る (#vault) + 編集 placeholder (T-D-Vault-Edit で実装、現状 disabled) +
 *     削除 (confirm dialog → DELETE → #vault navigate)
 *   - 復号失敗 / 404 / 403 / 401 で適切な i18n error 表示
 *
 * スコープ外:
 *   - Security sidebar (パスワード強度メータ + 再利用 + 漏洩 + 最終ログイン):
 *     T-E Reports と同期で Phase 4
 *   - 履歴 (パスワード変更履歴): backend に変更 history テーブルが無いため別 commit
 *   - 添付ファイル / アーカイブ / コレクション追加: Phase 5+
 */

interface DetailKVProps {
  label: string
  value: string
  mono?: boolean
  hidden?: boolean
  onToggleVisibility?: () => void
  onCopy?: () => void
  copyLabel?: string
  externalHref?: string
  externalLabel?: string
}

const DetailKV = ({
  label,
  value,
  mono,
  hidden,
  onToggleVisibility,
  onCopy,
  copyLabel,
  externalHref,
  externalLabel,
}: DetailKVProps): JSX.Element => (
  <div className="kv">
    <div className="kv-meta">
      <div className="k">{label}</div>
      <div className={`v${mono ? ' mono' : ''}`}>{hidden ? '••••••••••••' : value}</div>
    </div>
    <div className="acts">
      {onToggleVisibility && (
        <button
          type="button"
          className="icon-btn"
          onClick={onToggleVisibility}
          aria-label={hidden ? 'show' : 'hide'}
        >
          <Icon name={hidden ? 'eye' : 'eye-off'} />
        </button>
      )}
      {externalHref && (
        <a
          className="icon-btn"
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={externalLabel ?? 'open'}
        >
          <Icon name="external" />
        </a>
      )}
      {onCopy && copyLabel && (
        <button type="button" className="icon-btn" onClick={onCopy} aria-label={copyLabel}>
          <Icon name="copy" />
        </button>
      )}
    </div>
  </div>
)

/**
 * TOTP の 6 桁コードを 3 桁ずつ視認しやすく分割する (`123456` → `123 456`)。
 * 8 桁になっても 4-4 で分割する。
 */
const formatTotpCode = (code: string): string => {
  if (code.length <= 4) return code
  const mid = Math.floor(code.length / 2)

  return `${code.slice(0, mid)} ${code.slice(mid)}`
}

interface TotpRowProps {
  /** Base32 シークレット (Vault payload に保存されているもの)。 */
  secret: string

  /** コピーボタン押下時のハンドラ。`undefined` のときは何もしない (disabled)。 */
  onCopyCode: (code: string | undefined) => void
}

const isTotpActive = (s: UseTotpState): s is Extract<UseTotpState, { kind: 'active' }> =>
  s.kind === 'active'

/**
 * 1 アイテム分の TOTP 行 (T-D-Vault-TOTP)。
 *
 * `useTotp` で 1 秒ごとに rotation を計算し、6 桁コードと progress bar を描画。
 * コピーボタンは **secret ではなく現在の OTP コード自身** をクリップボードに置く。
 */
const TotpRow = ({ secret, onCopyCode }: TotpRowProps): JSX.Element => {
  const { t } = useTranslation()
  const state = useTotp(secret)

  return (
    <div className="kv totp-kv">
      <div className="kv-meta">
        <div className="k">{t('vault.detail.field.totp_code')}</div>
        {isTotpActive(state) ? (
          <div
            className="totp-row-display"
            role="progressbar"
            aria-label={t('vault.detail.totp.expires_in', {
              sec: Math.ceil(state.remainingMs / 1000),
            })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round((1 - state.progress) * 100)}
          >
            <span className="v mono totp-code">{formatTotpCode(state.code)}</span>
            <TotpRing progress={state.progress} remainingMs={state.remainingMs} />
          </div>
        ) : (
          <div className="v mono totp-code">— — —</div>
        )}
        {state.kind === 'error' && <div className="totp-error">{t(state.messageKey)}</div>}
      </div>
      <div className="acts">
        <button
          type="button"
          className="icon-btn"
          disabled={!isTotpActive(state)}
          onClick={() => onCopyCode(isTotpActive(state) ? state.code : undefined)}
          aria-label={t('vault.detail.action.copy_totp_code')}
        >
          <Icon name="copy" />
        </button>
      </div>
    </div>
  )
}

export interface PayloadSectionsProps {
  payload: VaultItemPayload
  isPwVisible: boolean
  onTogglePwVisible: () => void
  onCopy: (kind: string, value: string | undefined) => void
}

export const PayloadSections = ({
  payload,
  isPwVisible,
  onTogglePwVisible,
  onCopy,
}: PayloadSectionsProps): JSX.Element => {
  const { t } = useTranslation()

  if (payload.type === 'login') {
    return (
      <div className="detail-section">
        <h4>{t('vault.detail.section.login')}</h4>
        {payload.username && (
          <DetailKV
            label={t('vault.detail.field.username')}
            value={payload.username}
            onCopy={() => onCopy('username', payload.username)}
            copyLabel={t('vault.detail.action.copy_username')}
          />
        )}
        {payload.password && (
          <DetailKV
            label={t('vault.detail.field.password')}
            value={payload.password}
            mono
            hidden={!isPwVisible}
            onToggleVisibility={onTogglePwVisible}
            onCopy={() => onCopy('password', payload.password)}
            copyLabel={t('vault.detail.action.copy_password')}
          />
        )}
        {payload.totpSecret && (
          <TotpRow secret={payload.totpSecret} onCopyCode={code => onCopy('totp_code', code)} />
        )}
        {payload.uris?.map((u, i) => (
          <DetailKV
            key={i}
            label={t('vault.detail.field.url')}
            value={u.uri}
            mono
            externalHref={u.uri}
            externalLabel={t('vault.detail.action.open_url')}
            onCopy={() => onCopy('url', u.uri)}
            copyLabel={t('vault.detail.action.copy_url')}
          />
        ))}
      </div>
    )
  }
  if (payload.type === 'card') {
    return (
      <div className="detail-section">
        <h4>{t('vault.detail.section.card')}</h4>
        {payload.cardNumber && (
          <DetailKV
            label={t('vault.detail.field.card_number')}
            value={payload.cardNumber}
            mono
            onCopy={() => onCopy('card_number', payload.cardNumber)}
            copyLabel={t('vault.detail.action.copy_generic')}
          />
        )}
        {payload.cardHolder && (
          <DetailKV
            label={t('vault.detail.field.card_holder')}
            value={payload.cardHolder}
            onCopy={() => onCopy('card_holder', payload.cardHolder)}
            copyLabel={t('vault.detail.action.copy_generic')}
          />
        )}
        {payload.cardExpiry && (
          <DetailKV
            label={t('vault.detail.field.card_expiry')}
            value={payload.cardExpiry}
            mono
            onCopy={() => onCopy('card_expiry', payload.cardExpiry)}
            copyLabel={t('vault.detail.action.copy_generic')}
          />
        )}
        {payload.cardCvv && (
          <DetailKV
            label={t('vault.detail.field.card_cvv')}
            value={payload.cardCvv}
            mono
            hidden
            onCopy={() => onCopy('card_cvv', payload.cardCvv)}
            copyLabel={t('vault.detail.action.copy_generic')}
          />
        )}
      </div>
    )
  }
  if (payload.type === 'note') {
    return (
      <div className="detail-section">
        <h4>{t('vault.detail.section.note')}</h4>
        <div className="vault-detail-note-body">{payload.body}</div>
      </div>
    )
  }

  // TASK-WEB-004: 新スキーマ (fullName + 4 新フィールド) を優先表示。
  // 旧 item は fullName が無いので legacy `lastName + ' ' + firstName` で合成。
  // 表示順 ID番号 → 氏名 → 性別 → 生年月日 → 住所 → 電話 → メール (form と同じ)。
  const legacyFullName = [payload.lastName, payload.firstName]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(' ')
    .trim()
  const displayFullName = payload.fullName ?? (legacyFullName !== '' ? legacyFullName : undefined)

  return (
    <div className="detail-section">
      <h4>{t('vault.detail.section.identity')}</h4>
      {payload.idNumber && (
        <DetailKV
          label={t('vault.detail.field.id_number')}
          value={payload.idNumber}
          onCopy={() => onCopy('id_number', payload.idNumber)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {displayFullName && (
        <DetailKV
          label={t('vault.detail.field.full_name')}
          value={displayFullName}
          onCopy={() => onCopy('full_name', displayFullName)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {payload.gender && (
        <DetailKV
          label={t('vault.detail.field.gender')}
          value={payload.gender}
          onCopy={() => onCopy('gender', payload.gender)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {payload.dateOfBirth && (
        <DetailKV
          label={t('vault.detail.field.date_of_birth')}
          value={payload.dateOfBirth}
          onCopy={() => onCopy('date_of_birth', payload.dateOfBirth)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {payload.address && (
        <DetailKV
          label={t('vault.detail.field.address')}
          value={payload.address}
          onCopy={() => onCopy('address', payload.address)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {payload.phone && (
        <DetailKV
          label={t('vault.detail.field.phone')}
          value={payload.phone}
          onCopy={() => onCopy('phone', payload.phone)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
      {payload.email && (
        <DetailKV
          label={t('vault.detail.field.email')}
          value={payload.email}
          onCopy={() => onCopy('email', payload.email)}
          copyLabel={t('vault.detail.action.copy_generic')}
        />
      )}
    </div>
  )
}

export interface CustomFieldsSectionProps {
  fields: readonly VaultCustomField[]
  onCopy: (kind: string, value: string | undefined) => void
}

/**
 * #1 ユーザー定義カスタムフィールドの一覧 (全 type 共通)。
 *
 * - text: 値をそのまま表示 + copy
 * - hidden: 既定はマスク表示。eye アイコンで個別に表示切替 (index ごとに保持)
 * - boolean: 真偽値ラベルのみ表示 (copy 不要)
 */
export const CustomFieldsSection = ({
  fields,
  onCopy,
}: CustomFieldsSectionProps): JSX.Element | null => {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState<readonly number[]>([])

  if (fields.length === 0) return null

  const toggle = (index: number): void => {
    setRevealed(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]))
  }

  return (
    <div className="detail-section">
      <h4>{t('vault.detail.section.custom_fields')}</h4>
      {fields.map((f, i) => {
        if (f.type === 'boolean') {
          return (
            <div key={i} className="kv">
              <div className="kv-meta">
                <div className="k">{f.name}</div>
                <div className="v">
                  {t(
                    f.value === 'true'
                      ? 'vault.detail.custom_field.bool_true'
                      : 'vault.detail.custom_field.bool_false',
                  )}
                </div>
              </div>
            </div>
          )
        }

        const isHidden = f.type === 'hidden' && !revealed.includes(i)

        return (
          <DetailKV
            key={i}
            label={f.name}
            value={f.value}
            hidden={isHidden}
            onToggleVisibility={f.type === 'hidden' ? () => toggle(i) : undefined}
            onCopy={() => onCopy('generic', f.value)}
            copyLabel={t('vault.detail.action.copy_generic')}
          />
        )
      })}
    </div>
  )
}

const formatTimestamp = formatDateTimeJst

const goVault = (): void => {
  window.location.hash = 'vault'
}

export interface VaultDetailPageProps {
  /** route から抽出した item id (App.tsx 側で `vault/:id` を分解して渡す)。 */
  id: string
}

export const VaultDetailPage = ({ id }: VaultDetailPageProps): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const detail = useVaultDetail(id)
  const { folders } = useFolders()
  const { state: domainsState } = useEquivalentDomains()
  const [isPwVisible, setIsPwVisible] = useState(false)

  const domainGroups = domainsState.kind === 'ready' ? domainsState.groups : []

  // deleted 状態に達したら #vault に navigate して終了 (副作用は effect で 1 度だけ)
  useEffect(() => {
    if (detail.state.kind === 'deleted') {
      goVault()
    }
  }, [detail.state.kind])

  const onCopy = async (kind: string, value: string | undefined): Promise<void> => {
    if (!value) return
    try {
      await copyToClipboard(value)
      showToast({
        titleKey: `vault.detail.toast.copied_${kind}`,
        titleVars: { ttl: getEffectiveClipboardTtlSec() },
        tone: 'success',
        dedupe: true,
      })
    } catch {
      showToast({
        titleKey: 'vault.toast.copy_failed',
        tone: 'danger',
        dedupe: true,
      })
    }
  }

  if (detail.state.kind === 'loading' || detail.state.kind === 'deleted') {
    return (
      <div>
        <PageHead title={t('vault.detail.loading')} />
      </div>
    )
  }

  if (detail.state.kind === 'error') {
    return (
      <div>
        <PageHead
          title={t('vault.detail.error_title')}
          actions={
            <button type="button" className="btn btn-ghost" onClick={goVault}>
              <Icon name="arrow-left" aria-hidden="true" />
              {t('vault.detail.back')}
            </button>
          }
        />
        <div className="page-body">
          <Notice tone="danger" ico="alert">
            {t(detail.state.messageKey)}
          </Notice>
        </div>
      </div>
    )
  }

  const item = detail.state.item
  const isConfirming = detail.state.kind === 'confirm-delete'
  const isDeleting = detail.state.kind === 'deleting'
  const isDeleteFlow = isConfirming || isDeleting

  // このアイテムが属するドメインルールグループ (#settings/domain)。
  const domainGroupId = groupIdForItem(item, domainGroups)

  const domainGroup =
    domainGroupId !== null ? (domainGroups.find(g => g.id === domainGroupId) ?? null) : null

  // 編集 / 削除はカード内末尾の `.vault-form-actions` (区切り線付き) に置き、
  // 編集画面とレイアウトを揃える。ヘッダには戻るボタンのみ残す。
  const headerActions: ReactNode = (
    <button type="button" className="btn btn-secondary" onClick={goVault}>
      <Icon name="arrow-left" aria-hidden="true" />
      {t('vault.detail.back')}
    </button>
  )

  return (
    <div>
      <PageHead
        title={item.payload.name}
        sub={t(`vault.type.${item.type}`)}
        actions={headerActions}
      />
      <div className="page-body">
        <div className="card vault-detail-card">
          <div className="detail-head">
            <VaultItemAvatar name={item.payload.name} size="lg" />
            <div className="meta">
              <div className="nm">
                {item.payload.name}
                {item.payload.favorite === true && (
                  <span className="vault-row-fav" aria-hidden="true">
                    <Icon name="star" />
                  </span>
                )}
              </div>
              <div className="vault-detail-head-tags">
                <span className="tag">{t(`vault.type.${item.type}`)}</span>
                <VaultStrengthBadge item={item} />
                {item.folderId != null && (
                  <span
                    className="tag vault-detail-folder-tag"
                    data-testid="vault-detail-folder-tag"
                  >
                    <Icon name="folder" aria-hidden="true" />
                    {folders.find(f => f.id === item.folderId)?.name ?? t('vault.folder.none')}
                  </span>
                )}
                {domainGroup !== null && (
                  <span
                    className="tag vault-group-tag"
                    data-testid="vault-detail-domain-group"
                    title={domainGroup.domains.join(' · ')}
                  >
                    <Icon name="link" aria-hidden="true" />
                    {domainGroup.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          <PayloadSections
            payload={item.payload}
            isPwVisible={isPwVisible}
            onTogglePwVisible={() => setIsPwVisible(v => !v)}
            onCopy={(kind, value) => void onCopy(kind, value)}
          />

          {item.payload.fields && item.payload.fields.length > 0 && (
            <CustomFieldsSection
              fields={item.payload.fields}
              onCopy={(kind, value) => void onCopy(kind, value)}
            />
          )}

          {item.payload.notes && (
            <div className="detail-section">
              <h4>{t('vault.detail.section.notes')}</h4>
              <div className="vault-detail-note-body">{item.payload.notes}</div>
            </div>
          )}

          <div className="detail-section vault-detail-meta">
            <div className="kv">
              <div className="kv-meta">
                <div className="k">{t('vault.detail.field.updated_at')}</div>
                <div className="v mono">{formatTimestamp(item.updatedAt)}</div>
              </div>
            </div>
            <div className="kv">
              <div className="kv-meta">
                <div className="k">{t('vault.detail.field.created_at')}</div>
                <div className="v mono">{formatTimestamp(item.createdAt)}</div>
              </div>
            </div>
          </div>

          <div className="vault-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={detail.requestDelete}
              disabled={isDeleteFlow}
              data-testid="vault-detail-delete"
            >
              <Icon name="trash" aria-hidden="true" />
              {t('vault.detail.delete')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                window.location.hash = `vault/${item.id}/edit`
              }}
              disabled={isDeleteFlow}
              data-testid="vault-detail-edit"
            >
              <Icon name="edit" aria-hidden="true" />
              {t('vault.detail.edit')}
            </button>
          </div>
        </div>
      </div>

      {isDeleteFlow && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vault-delete-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="vault-delete-title">{t('vault.detail.delete_dialog.title')}</h3>
            <p>{t('vault.detail.delete_dialog.body', { name: item.payload.name })}</p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={detail.cancelDelete}
                disabled={isDeleting}
              >
                {t('vault.detail.delete_dialog.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void detail.confirmDelete()}
                disabled={isDeleting}
              >
                <Icon name="trash" aria-hidden="true" />
                {isDeleting
                  ? t('vault.detail.delete_dialog.deleting')
                  : t('vault.detail.delete_dialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
